import React, { CSSProperties, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';

import './project-cards.scss';

type ProjectCardProps = PropsWithChildren<{}>;
type ProjectCardTitleProps = PropsWithChildren<{}>;
type ProjectCardCompanyProps = PropsWithChildren<{}>;
type ProjectCardDescProps = PropsWithChildren<{}>;

type ProjectCardMoreProps = PropsWithChildren<{
  expanded?: boolean;
}>;

type ProjectCardImageProps = PropsWithChildren<{
  src: string,
  alt?: string
}>;

interface ProjectCardPropsMap {
  ProjectCardTitle: ProjectCardTitleProps;
  ProjectCardCompany: ProjectCardCompanyProps;
  ProjectCardDesc: ProjectCardDescProps;
  ProjectCardMore: ProjectCardMoreProps;
  ProjectCardImage: ProjectCardImageProps;
}

type ProjectCardsProps = PropsWithChildren<{}>;

type ProjectCardIndexedProps = PropsWithChildren<{
  expanded: boolean,
  onExpand: () => void,
  delay: number
}>;

type ProjectCardBaseComponent<T> = React.FC<T> & {
  componentName: string
};

type ProjectCardTitleComponent = ProjectCardBaseComponent<ProjectCardTitleProps>;
type ProjectCardCompanyComponent = ProjectCardBaseComponent<ProjectCardCompanyProps>;
type ProjectCardDescComponent = ProjectCardBaseComponent<ProjectCardDescProps>;
type ProjectCardMoreComponent = ProjectCardBaseComponent<ProjectCardMoreProps>;
type ProjectCardImageComponent = ProjectCardBaseComponent<ProjectCardImageProps>;
type ProjectCardComponent = ProjectCardBaseComponent<ProjectCardProps>;

type ProjectCardIndexedComponent = React.FC<ProjectCardIndexedProps>;
type ProjectCardsComponent = React.FC<ProjectCardsProps> & {
  Card: ProjectCardComponent,
  Image: ProjectCardImageComponent,
  Title: ProjectCardTitleComponent,
  Company: ProjectCardCompanyComponent,
  Desc: ProjectCardDescComponent,
  More: ProjectCardMoreComponent
};

const ProjectCards: ProjectCardsComponent = (props: ProjectCardsProps) => {

  const [ viewMore, setViewMore ] = useState<boolean>(false);
  const [ expanded, setExpanded ] = useState<number>(0);
  const [ width, setWidth ] = useState<number>(0);

  useEffect(() => {
    const onResize = () => {
      if (width !== document.documentElement.clientWidth) {
        setWidth(document.documentElement.clientWidth);
        setExpanded(0);
      }
    }

    onResize();

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);

  }, [ width ]);

  const children = React.Children.toArray(props.children);

  const indexedCards = (incDelay: boolean, start: number, end?: number) => {
    return children.slice(start, end).map((child: React.ReactNode, index: number) => {
      if ((!React.isValidElement(child)) || ((child.type as ProjectCardBaseComponent<unknown>).componentName !== 'ProjectCard')) {
        return null;
      }

      const cardKey = start + index + 1;

      return (
        <ProjectCardIndexed key={index} delay={incDelay ? ((index + 1) * 100) : 100} expanded={cardKey === expanded} onExpand={() => { setExpanded(cardKey !== expanded ? cardKey : 0); }}>
          {child.props.children}
        </ProjectCardIndexed>
      );
    });
  };

  return (
    <div className="component-project-cards">
      <div className="container">
        <div className="row">

          {indexedCards(true, 0, 4)}
          {viewMore && indexedCards(false, 4)}

        </div>

        {!viewMore &&
          <div className="row view-more" data-aos="fade-in" data-aos-delay={400}>
            <div className="col-12 mt-4 text-center">
              <a href="#projects" onClick={() => { setViewMore(true); }}>See more</a>
            </div>
          </div>
        }

      </div>
    </div>
  );

};

const ProjectCardIndexed: ProjectCardIndexedComponent = (props: ProjectCardIndexedProps) => {

  const [ touched, setTouched ] = useState<boolean>(false);

  const children = React.Children.toArray(props.children);

  const getChild = <K extends keyof ProjectCardPropsMap>(displayName: K, props?: ProjectCardPropsMap[K]): React.ReactNode => {
    const foundChild = children.find((child: React.ReactNode): boolean => {
      return ((React.isValidElement(child)) && ((child.type as ProjectCardBaseComponent<unknown>).componentName === displayName));
    });

    if (!React.isValidElement(foundChild)) {
      return null;
    }

    return React.cloneElement(foundChild, props);
  };

  return (
    <div className={`project-card col-lg-${props.expanded ? '12' : '6'} mt-4 ${props.expanded ? 'expanded' : 'closed'} ${touched ? 'touched' : ''}`}>
      <div className="project" data-aos="zoom-in" data-aos-delay={props.delay}>

        <div className="d-flex flex-row align-items-start">
          <div className="d-none d-md-block image">{getChild('ProjectCardImage')}</div>
          <div className="info">
            {getChild('ProjectCardCompany')}
            {getChild('ProjectCardTitle')}
            {getChild('ProjectCardDesc')}
            {getChild('ProjectCardMore', { expanded: props.expanded })}
          </div>
        </div>

        <div className="d-flex flex-row justify-content-end">
          <div className="expand text-end">
            <button onClick={() => { setTouched(true); props.onExpand() }}><i className={`bi ${props.expanded ? 'bi-arrow-up-left-square' : 'bi-arrow-down-right-square'}`}></i></button>
          </div>
        </div>

      </div>
    </div>
  );

};

const ProjectCard: ProjectCardComponent = (props: ProjectCardProps) => {
  // This is the card we expose. It is simply a wrapper for the header, company, desc and more components.
  // We could validate for those components if needed.
  // We take the children of this component, and inject them in to an indexed version: ProjectCardIndexed.
  // The main purpose of this, is so that we do not need to expose an index prop, which is injected at the ProjectCards level.

  return (
    <React.Fragment>
      {props.children}
    </React.Fragment>
  );
};

const ProjectCardImage: ProjectCardImageComponent = (props: ProjectCardImageProps) => {
  return (
    <img src={props.src} className="img-fluid" alt={props.alt || ''} />
  );
};

const ProjectCardTitle: ProjectCardTitleComponent = (props: ProjectCardTitleProps) => {
  return (
    <span className="title">{props.children}</span>
  );
};

const ProjectCardCompany: ProjectCardCompanyComponent = (props: ProjectCardCompanyProps) => {
  return (
    <h4>{props.children}</h4>
  );
};

const ProjectCardDesc: ProjectCardDescComponent = (props: ProjectCardDescProps) => {
  return (
    <p className="desc">{props.children}</p>
  );
};

const ProjectCardMore: ProjectCardMoreComponent = (props: ProjectCardMoreProps) => {

  const ref = useRef<HTMLDivElement>(null);
  const [ scrollHeight, setScrollHeight ] = useState<number>(0);

  useLayoutEffect(() => {
    if (props.expanded && ref.current) {
      const card = ref.current.closest('.project-card');
      const row = ref.current.closest('.row');

      if (card && row) {
        const clone = card.cloneNode(true) as HTMLElement;

        clone.className = clone.className.replace('col-lg-6', 'col-lg-12').replace('closed', 'expanded');
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        clone.style.pointerEvents = 'none';
        clone.style.zIndex = '-100';
        clone.style.width = `${row.clientWidth}px`;

        row.appendChild(clone);

        const moreClone = clone.querySelector('.more') as HTMLElement;
        if (moreClone) {
          moreClone.style.height = 'auto';
          setScrollHeight(moreClone.scrollHeight);
        }

        row.removeChild(clone);
      }
    }
  }, [ props.expanded ]);

  return (
    <div ref={ref} className="more" style={{
      '--height': `${scrollHeight}px`
    } as CSSProperties}>
      {props.children}
    </div>
  );
};

ProjectCard.componentName = 'ProjectCard';
ProjectCardImage.componentName = 'ProjectCardImage';
ProjectCardTitle.componentName = 'ProjectCardTitle';
ProjectCardCompany.componentName = 'ProjectCardCompany';
ProjectCardDesc.componentName = 'ProjectCardDesc';
ProjectCardMore.componentName = 'ProjectCardMore';

ProjectCards.Card = ProjectCard;
ProjectCards.Image = ProjectCardImage;
ProjectCards.Title = ProjectCardTitle;
ProjectCards.Company = ProjectCardCompany;
ProjectCards.Desc = ProjectCardDesc;
ProjectCards.More = ProjectCardMore;

export default ProjectCards;